import { useId } from 'react'
import { Box, Chip, useTheme } from '@mui/material'

import { ChartAreaGradient, chartSeriesColor } from 'components/analytics/chartStyle'

import type { SeoMarket, SeoSourceState } from 'services'

import { useTone } from '../shared'

/** VRG-643 — formatting + tiny visuals shared by the SEO & AEO page (`/internal/gtm/seo`). */

export const fmt = (n: number | null | undefined, digits = 0): string =>
  n === null || n === undefined || Number.isNaN(n)
    ? '—'
    : n.toLocaleString('en-US', {
        maximumFractionDigits: digits,
        minimumFractionDigits: digits,
      })

export const fmtPct = (ratio: number | null | undefined, digits = 1): string =>
  ratio === null || ratio === undefined ? '—' : `${(ratio * 100).toFixed(digits)}%`

export const fmtPos = (p: number | null | undefined): string =>
  p === null || p === undefined ? '—' : p.toFixed(1)

export const fmtUsd = (n: number | null | undefined): string =>
  n === null || n === undefined
    ? '—'
    : `$${n.toLocaleString('en-US', {
        // Whole dollars once the figure is large enough that cents are noise.
        minimumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
        maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
      })}`

/** "−2.3" style delta, signed, or null when either side is missing. */
export const delta = (now: number | null | undefined, before: number | null | undefined) =>
  now === null || now === undefined || before === null || before === undefined ? null : now - before

/** "New York time" for the default zone; the zone id otherwise (VRG-676). */
export const zoneLabel = (timeZone: string): string =>
  timeZone === 'America/New_York' ? 'New York time' : timeZone

const time12 = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number)
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`
}

/**
 * The slot in words, from the settings in force: "every other Thursday at 2:00 pm New York
 * time" at the defaults, "every 28 days on Thursdays at …" otherwise.
 */
export const scheduleLabel = (s: { weekday: string; localTime: string; timeZone: string }, intervalDays: number): string => {
  const day = s.weekday.charAt(0).toUpperCase() + s.weekday.slice(1)
  const when = `${time12(s.localTime)} ${zoneLabel(s.timeZone)}`
  if (intervalDays === 7) return `every ${day} at ${when}`
  if (intervalDays === 14) return `every other ${day} at ${when}`
  return `every ${intervalDays} days on ${day}s at ${when}`
}

/** A next-run instant in the schedule's zone, with its abbreviation: "Thu 22 Oct, 2:00 pm EDT". */
export const fmtScheduled = (iso: string | null | undefined, timeZone: string): string => {
  if (!iso) return '—'
  const d = new Date(iso)
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(d)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  // en-GB names the New York zone "GMT-4"; en-US names it "EDT" / "EST", which is what a
  // reader in New York expects to see.
  const zone =
    new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName')?.value ?? get('timeZoneName')
  return `${get('weekday')} ${get('day')} ${get('month')}, ${get('hour')}:${get('minute')} ${get('dayPeriod').toLowerCase()} ${zone}`
}

export const marketLabel = (m: SeoMarket) =>
  `${m.name}${m.mode === 'competitors_only' ? ' (competitors only)' : ''}`

/**
 * The sentence for a source that is not simply ok. Missing data is SAID, never zeroed.
 * An `ok` source with a message is still worth a line: that is how fixture data (the
 * Website tab's log driver) arrives, and an unlabelled fixture beside real Search Console
 * figures would read as real traffic.
 */
export const sourceText = (name: string, s: SeoSourceState | undefined): string | null => {
  if (!s) return null
  const why = s.message ? ` ${s.message}` : ''
  switch (s.status) {
    case 'ok':
      return s.message ? `${name}: ${s.message}.` : null
    case 'not_configured':
      return `${name} is not configured on this API: nothing is fetched from it here, and any figures from it were stored by earlier runs.${why}`
    case 'disabled':
      return `${name} is turned off.${why}`
    case 'error':
      return `${name} failed on the last read.${why}`
  }
  return null
}

/**
 * A rank position as a chip: 1–3 strong, 4–10 page one, 11+ beyond page one, null "not
 * in the checked depth" (an em-dash, never a zero — a missing rank is not rank 0).
 */
export const PositionChip = ({
  position,
  highlight,
}: {
  position: number | null
  highlight?: boolean
}) => {
  const toneOf = useTone()
  if (position === null)
    return (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        —
      </Box>
    )
  // VERA tones via statusTone (green / blue / gray), not MUI's success / info defaults.
  const tone = toneOf(position <= 3 ? 'positive' : position <= 10 ? 'info' : 'neutral').color
  return (
    <Chip
      size="small"
      label={position}
      variant="outlined"
      sx={{
        height: 22,
        minWidth: 32,
        fontSize: 12,
        fontWeight: highlight ? 700 : 500,
        color: tone,
        borderColor: tone,
      }}
    />
  )
}

/**
 * A 64×20 inline trend line. Deliberately axis-less: it answers "rising, flat or falling",
 * and the number beside it carries the magnitude. Nulls break the line rather than being
 * drawn as zero. `invert` flips the y-axis for ranks, where smaller is better.
 *
 * Drawn in the house chart style at table-cell scale: the brand series colour, a faint
 * gradient of the same colour under each run (the `/analytics` area's 0.3 -> 0), and a brand
 * end dot. Inline SVG rather than recharts on purpose: a table renders dozens of these, and
 * a ResponsiveContainer per cell would buy nothing at 64px wide.
 */
export const Sparkline = ({
  values,
  width = 64,
  height = 20,
  invert = false,
  label,
}: {
  values: Array<number | null>
  width?: number
  height?: number
  invert?: boolean
  label?: string
}) => {
  const theme = useTheme()
  // useId's `:r0:` form is not a safe url() fragment, so strip it to word characters.
  const gradientId = `spark-${useId().replace(/[^a-zA-Z0-9]/g, '')}`
  const nums = values.filter((v): v is number => v !== null)
  if (nums.length < 2)
    return (
      <Box component="span" sx={{ color: 'text.disabled', fontSize: 12 }}>
        —
      </Box>
    )
  const color = chartSeriesColor(theme)
  const min = Math.min(...nums)
  const max = Math.max(...nums)
  const span = max - min || 1
  const step = values.length > 1 ? (width - 2) / (values.length - 1) : 0
  const x = (i: number) => 1 + i * step
  const y = (v: number) => {
    const t = (v - min) / span
    return 1 + (invert ? t : 1 - t) * (height - 2)
  }
  // Runs of consecutive non-null points; a null ends one.
  const runs: Array<Array<[number, number]>> = []
  let current: Array<[number, number]> = []
  values.forEach((v, i) => {
    if (v === null) {
      if (current.length) runs.push(current)
      current = []
      return
    }
    current.push([x(i), y(v)])
  })
  if (current.length) runs.push(current)
  const line = (run: Array<[number, number]>) =>
    run.map(([px, py], i) => `${i ? 'L' : 'M'}${px.toFixed(1)},${py.toFixed(1)}`).join('')
  // The same run closed down to the bottom edge, for the fill under it.
  const area = (run: Array<[number, number]>) =>
    `${line(run)}L${run[run.length - 1][0].toFixed(1)},${height}L${run[0][0].toFixed(1)},${height}Z`
  const last = values.length - 1
  const lastV = values[last]
  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label={label ?? 'trend'}
      style={{ display: 'block' }}
    >
      <defs>
        <ChartAreaGradient id={gradientId} color={color} />
      </defs>
      {runs.map((run) => (
        <g key={`${run[0][0]}`}>
          {run.length > 1 ? <path d={area(run)} fill={`url(#${gradientId})`} stroke="none" /> : null}
          <path d={line(run)} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" />
        </g>
      ))}
      {lastV !== null && lastV !== undefined ? (
        <circle cx={x(last)} cy={y(lastV)} r={2} fill={color} />
      ) : null}
    </svg>
  )
}
